/**
 * Interactive screen canvas — the TV/phone surface scaled to fit the panel,
 * with zones rendered as positioned rects you can click to select, drag to
 * move, and resize via corner handles (and draw new zones by dragging empty
 * space). Coordinates stay in surface pixels (TV 1920×1080 / phone 390×844);
 * pointer → surface mapping uses the SVG CTM so it's correct at any scale.
 * Grid-snap is renderer-side (TDD §7.2).
 *
 * Ported from Anvil's ScreenEditor canvas.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { Screen, Zone } from '@volley/crucible-ipc-types';
import { SURFACE_DIMENSIONS, colorFor } from './lib/zone-kinds';

const GRID = 8;
const HANDLE_PX = 9; // on-screen half-size of a resize handle, kept constant

let zoneCounter = 0;
function newZoneId(): string {
  zoneCounter += 1;
  return `zone-${Date.now()}-${zoneCounter}`;
}

const snap = (v: number): number => Math.round(v / GRID) * GRID;
const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

type Corner = 'nw' | 'ne' | 'sw' | 'se';
type Drag =
  | { kind: 'none' }
  | { kind: 'move'; zoneId: string; offsetX: number; offsetY: number }
  | { kind: 'resize'; zoneId: string; corner: Corner; anchorX: number; anchorY: number }
  | { kind: 'draw'; startX: number; startY: number };

export interface ScreenCanvasProps {
  screen: Screen;
  onChange: (screen: Screen) => void;
  selectedZoneId: string | null;
  onSelectZone: (id: string | null) => void;
}

export function ScreenCanvas({
  screen,
  onChange,
  selectedZoneId,
  onSelectZone,
}: ScreenCanvasProps): ReactElement {
  const dims = SURFACE_DIMENSIONS[screen.surface];
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [drag, setDrag] = useState<Drag>({ kind: 'none' });
  const [draft, setDraft] = useState<Zone | null>(null);
  // Display scale (surface units per screen pixel) so handles stay a constant
  // on-screen size regardless of how far the surface is scaled down.
  const [unitsPerPx, setUnitsPerPx] = useState(dims.width / 600);

  useLayoutEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const measure = (): void => {
      const w = svg.getBoundingClientRect().width;
      if (w > 0) setUnitsPerPx(dims.width / w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(svg);
    return () => ro.disconnect();
  }, [dims.width]);

  const handleU = HANDLE_PX * unitsPerPx; // handle half-size in surface units

  const toSurface = useCallback((clientX: number, clientY: number): { x: number; y: number } => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const p = pt.matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  }, []);

  // Global move/up listeners while dragging so it keeps tracking off-canvas.
  useEffect(() => {
    if (drag.kind === 'none') return;
    const onMove = (e: MouseEvent): void => {
      const { x, y } = toSurface(e.clientX, e.clientY);
      if (drag.kind === 'draw') {
        setDraft({
          id: '__draft__',
          x: Math.min(drag.startX, x),
          y: Math.min(drag.startY, y),
          width: Math.abs(x - drag.startX),
          height: Math.abs(y - drag.startY),
          label: '',
          kind: 'other',
          notes: '',
        });
      } else if (drag.kind === 'move') {
        const zone = screen.zones.find((z) => z.id === drag.zoneId);
        if (!zone) return;
        onChange({
          ...screen,
          zones: screen.zones.map((z) =>
            z.id === drag.zoneId
              ? {
                  ...z,
                  x: clamp(snap(x - drag.offsetX), 0, dims.width - z.width),
                  y: clamp(snap(y - drag.offsetY), 0, dims.height - z.height),
                }
              : z,
          ),
        });
      } else if (drag.kind === 'resize') {
        onChange({
          ...screen,
          zones: screen.zones.map((z) => {
            if (z.id !== drag.zoneId) return z;
            const x2 = clamp(snap(x), 0, dims.width);
            const y2 = clamp(snap(y), 0, dims.height);
            const minX = Math.min(drag.anchorX, x2);
            const maxX = Math.max(drag.anchorX, x2);
            const minY = Math.min(drag.anchorY, y2);
            const maxY = Math.max(drag.anchorY, y2);
            return {
              ...z,
              x: minX,
              y: minY,
              width: Math.max(GRID, maxX - minX),
              height: Math.max(GRID, maxY - minY),
            };
          }),
        });
      }
    };
    const onUp = (): void => {
      if (drag.kind === 'draw' && draft && draft.width >= GRID * 2 && draft.height >= GRID * 2) {
        const topZ = screen.zones.reduce((m, z) => Math.max(m, z.zIndex ?? 0), 0);
        const zone: Zone = {
          id: newZoneId(),
          x: snap(draft.x),
          y: snap(draft.y),
          width: snap(draft.width),
          height: snap(draft.height),
          label: 'New zone',
          kind: 'other',
          notes: '',
          zIndex: topZ + 1,
        };
        onChange({ ...screen, zones: [...screen.zones, zone] });
        onSelectZone(zone.id);
      }
      setDraft(null);
      setDrag({ kind: 'none' });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, draft, screen, dims.width, dims.height, onChange, onSelectZone, toSurface]);

  // Escape deselects the current zone.
  useEffect(() => {
    if (!selectedZoneId) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onSelectZone(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedZoneId, onSelectZone]);

  const startResize = (e: React.MouseEvent, zone: Zone, corner: Corner): void => {
    e.stopPropagation();
    onSelectZone(zone.id);
    // Anchor = the opposite corner (stays fixed during resize).
    const anchorX = corner === 'nw' || corner === 'sw' ? zone.x + zone.width : zone.x;
    const anchorY = corner === 'nw' || corner === 'ne' ? zone.y + zone.height : zone.y;
    setDrag({ kind: 'resize', zoneId: zone.id, corner, anchorX, anchorY });
  };

  const startMove = (e: React.MouseEvent, zone: Zone): void => {
    e.stopPropagation();
    onSelectZone(zone.id);
    const { x, y } = toSurface(e.clientX, e.clientY);
    setDrag({ kind: 'move', zoneId: zone.id, offsetX: x - zone.x, offsetY: y - zone.y });
  };

  const onCanvasMouseDown = (e: React.MouseEvent): void => {
    if (e.target !== svgRef.current) return; // clicked a zone/handle → its handler runs
    const { x, y } = toSurface(e.clientX, e.clientY);
    onSelectZone(null);
    setDrag({ kind: 'draw', startX: x, startY: y });
  };

  const corners: { corner: Corner; cursor: string }[] = [
    { corner: 'nw', cursor: 'nwse-resize' },
    { corner: 'ne', cursor: 'nesw-resize' },
    { corner: 'sw', cursor: 'nesw-resize' },
    { corner: 'se', cursor: 'nwse-resize' },
  ];
  const cornerPos = (z: Zone, c: Corner): { cx: number; cy: number } => ({
    cx: c === 'nw' || c === 'sw' ? z.x : z.x + z.width,
    cy: c === 'nw' || c === 'ne' ? z.y : z.y + z.height,
  });

  return (
    <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-crucible-bg p-4">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${dims.width} ${dims.height}`}
        width={dims.width}
        height={dims.height}
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          width: 'auto',
          height: 'auto',
          display: 'block',
        }}
        onMouseDown={onCanvasMouseDown}
        data-testid="design-screen-canvas"
      >
        {/* Chrome rects are non-interactive so clicks on empty canvas reach
            the <svg> itself (→ deselect + start drawing a new zone). */}
        <rect
          x={0}
          y={0}
          width={dims.width}
          height={dims.height}
          fill="#16161a"
          style={{ pointerEvents: 'none' }}
        />
        <rect
          x={1}
          y={1}
          width={dims.width - 2}
          height={dims.height - 2}
          fill="none"
          stroke="#2a2a32"
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
          rx={12}
          style={{ pointerEvents: 'none' }}
        />

        {[...screen.zones]
          .sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0))
          .map((z) => {
            const color = colorFor(z.kind);
            const selected = z.id === selectedZoneId;
            return (
              <g key={z.id}>
                <rect
                  x={z.x}
                  y={z.y}
                  width={z.width}
                  height={z.height}
                  fill={color}
                  fillOpacity={selected ? 0.28 : 0.16}
                  stroke={color}
                  strokeWidth={selected ? 3 : 2}
                  vectorEffect="non-scaling-stroke"
                  rx={6}
                  style={{ cursor: 'move' }}
                  onMouseDown={(e) => startMove(e, z)}
                />
                <text
                  x={z.x + 8 * unitsPerPx}
                  y={z.y + 18 * unitsPerPx}
                  fill={color}
                  fontSize={13 * unitsPerPx}
                  fontWeight={600}
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                >
                  {z.label || z.kind}
                </text>
                {selected &&
                  corners.map(({ corner, cursor }) => {
                    const { cx, cy } = cornerPos(z, corner);
                    return (
                      <rect
                        key={corner}
                        x={cx - handleU}
                        y={cy - handleU}
                        width={handleU * 2}
                        height={handleU * 2}
                        fill="#ffffff"
                        stroke={color}
                        strokeWidth={1.5}
                        vectorEffect="non-scaling-stroke"
                        style={{ cursor }}
                        onMouseDown={(e) => startResize(e, z, corner)}
                      />
                    );
                  })}
              </g>
            );
          })}

        {draft && (
          <rect
            x={draft.x}
            y={draft.y}
            width={draft.width}
            height={draft.height}
            fill="#ffffff"
            fillOpacity={0.1}
            stroke="#ffffff"
            strokeDasharray="6 4"
            strokeWidth={1.5}
            vectorEffect="non-scaling-stroke"
            style={{ pointerEvents: 'none' }}
          />
        )}
      </svg>
    </div>
  );
}
